import React, { Component } from 'react';
import throttle from 'lodash.throttle';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { Helmet } from 'react-helmet';
import qs from 'qs';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { actions as jobsActions, operations as jobsOperations } from '@saucelabs/sl-module-jobs';
import { records as userRecords } from '@saucelabs/sl-module-user';
import { isSupportedFeature, constants as features } from '@saucelabs/sl-component-supported-feature';
import { is } from 'immutable';
import { TitleRow } from '@saucelabs/sl-component-breadcrumbs';
import { push as historyPush } from 'connected-react-router';

import { actions, constants, selectors, utils } from './../ducks/flatJobs';
import {
  constants as filterConstants,
  functions as filterFunctions,
  operations as filterOperations,
} from './../ducks/filters';
import {
  constants as sharedConstants,
  utils as sharedUtils,
} from './../ducks/shared';
import AutomatedJobsTab from './../components/AutomatedJobsTab';
import Styles from './FlatJobsContainer.styles';

const crumbsList = [
  { name: 'Automated' }, { name: 'Test Results' },
];

class FlatJobsContainer extends Component {
  constructor(props) {
    const {
      cloudType,
      experiments,
      filters,
      initializeFilters,
      isOpenSauceUser,
      isSubaccountDashboard,
      jobsTeamSharingFeatureEnabled,
      location,
      push,
      setCloudType,
      shouldFetchOrgUsers,
      user,
      username,
    } = props;
    super(props);
    this.pollers = [];
    this.throttledFetchData = throttle(this.fetchData, sharedConstants.API_FETCH_THROTTLE);

    const ownerFromQS = qs.parse(location.search, { ignoreQueryPrefix: true }).owner_filter;
    initializeFilters({
      fetchTeams: jobsTeamSharingFeatureEnabled,
      fetchUsers: shouldFetchOrgUsers,
      ownerFilter: ownerFromQS ?
        {
          type: filterConstants.FILTER_AUTOMATED_TESTS,
          data: { name: ownerFromQS, label: ownerFromQS, type: 'user' },
        } :
        undefined,
      username,
    });

    const status = filters.getIn([
      filterConstants.FILTER_AUTOMATED_TESTS,
      filterConstants.FILTER_TYPE_STATUS,
    ]);
    const ownership = filterFunctions.getOwnershipFilter({
      filterReference: filters,
      filterType: filterConstants.FILTER_AUTOMATED_TESTS,
      isOpenSauceUser,
      isSubaccountDashboard,
      username,
    });

    if (
      experiments?.get(constants.EXPERIMENT_RDC_JOB, '')?.toLowerCase() !==
      constants.EXPERIMENT_RDC_JOB.toLowerCase()
    ) {
      setCloudType(constants.VIRTUAL_DEVICE_CLOUD);
    }

    const wasSubscribed = user.get('was_subscribed');
    const wasTrial = user.get('was_trial');
    const isSaucePlanExpired = user.hasReadOnlyAccess() && (wasSubscribed || wasTrial);
    if (isSaucePlanExpired && user.hasTestObjectPlan()) {
      push('/dashboard/tests/rdc');
    }

    this.fetchData({
      cloudType,
      ownership,
      statusList: status,
    });
  }

  componentDidUpdate(prevProps) {
    const {
      cloudType: prevCloudType,
      filters: prevFilters,
      rdcOffset: prevRdcOffset,
    } = prevProps;
    const {
      clearRdcOffset,
      cloudType,
      filters,
      match,
      rdcOffset,
      setCloudType,
      // we dont have to use previous values of those, since they won't change in filters scope
      isSubaccountDashboard,
      isOpenSauceUser,
      username,
    } = this.props;

    const locationCloudType = constants.TABS[match?.params?.cloudType];
    if (locationCloudType && locationCloudType !== cloudType) {
      setCloudType(locationCloudType);
    }

    const cloudTypeChanged = prevCloudType !== locationCloudType;

    const prevOwnership = filterFunctions.getOwnershipFilter({
      filterReference: prevFilters,
      filterType: filterConstants.FILTER_AUTOMATED_TESTS,
      isSubaccountDashboard,
      isOpenSauceUser,
      username,
    });
    const ownership = filterFunctions.getOwnershipFilter({
      filterReference: filters,
      filterType: filterConstants.FILTER_AUTOMATED_TESTS,
      isSubaccountDashboard,
      isOpenSauceUser,
      username,
    });
    const prevStatus = prevFilters.getIn([
      filterConstants.FILTER_AUTOMATED_TESTS,
      filterConstants.FILTER_TYPE_STATUS,
    ]);
    const statusList = filters.getIn([
      filterConstants.FILTER_AUTOMATED_TESTS,
      filterConstants.FILTER_TYPE_STATUS,
    ]);

    const anyFilterChanged = (
      !is(prevStatus, statusList) ||
      (prevOwnership?.name !== ownership.name)
    );
    if (cloudTypeChanged || anyFilterChanged) {
      clearRdcOffset();
      this.throttledFetchData({
        cloudType,
        ownership,
        statusList,
        tabName: filterConstants.FILTER_AUTOMATED_TESTS,
      });
      return;
    }

    const pageChanged = prevRdcOffset !== rdcOffset;
    if (pageChanged) {
      this.throttledFetchData({
        cloudType,
        ownership,
        rdcOffset,
        statusList,
        tabName: filterConstants.FILTER_AUTOMATED_TESTS,
      });
    }
  }

  componentWillUnmount() {
    const { cleanup } = this.props;
    this.pollers.forEach(clearInterval);
    cleanup();
  }

  toggleStatusFilter = (status) => {
    const { trackedToggleStatus } = this.props;
    trackedToggleStatus(status, { type: 'job' });
  }

  callAPIWithIntervals(apiCalls) {
    this.pollers.forEach(clearInterval);
    apiCalls.forEach((apiCall) => apiCall());
    this.pollers = apiCalls.map((apiCall) => (
      setInterval(() => apiCall(), sharedConstants.API_FETCH_INTERVAL)
    ));
  }

  fetchData({ cloudType, ownership: argOwnership, rdcOffset, statusList }) {
    const {
      fetchAutomatedJobs,
      filters,
      getUPJobs,
      isOpenSauceUser,
      isSubaccountDashboard,
      jobsTeamSharingFeatureEnabled,
      organizationId,
      username,
    } = this.props;
    const propsOwnership = filterFunctions.getOwnershipFilter({
      filterReference: filters,
      filterType: filterConstants.FILTER_AUTOMATED_TESTS,
      isSubaccountDashboard,
      isOpenSauceUser,
      username,
    });
    const ownership = argOwnership || propsOwnership;
    // fetch tab specific data when tab is activated
    const pollers = [];

    if (cloudType === constants.REAL_DEVICE_CLOUD) {
      const params = utils.UPJobQueryParams({ offset: rdcOffset, statusList });
      pollers.push(() => getUPJobs(params));
    }

    if (cloudType === constants.VIRTUAL_DEVICE_CLOUD) {
      const params = sharedUtils.automatedJobQueryParams({
        jobsTeamSharingFeatureEnabled, organizationId, ownership, username, statusList,
      });
      pollers.push(() => fetchAutomatedJobs({ jobsTeamSharingFeatureEnabled, ...params }));
    }

    this.callAPIWithIntervals(pollers);
  }

  render() {
    const {
      automatedLoading,
      clearStatuses,
      cloudType,
      filters,
      filtersInitialized,
      isOpenSauceUser,
      isSubaccountDashboard,
      jobsTeamSharingEnabled,
      jobsTeamSharingFeatureEnabled,
      orgTeams,
      orgUsers,
      push,
      rdcOffset,
      selectOwner,
      togglePerformance,
      toggleStatus,
      user,
      username,
    } = this.props;

    const status = filters.getIn([
      filterConstants.FILTER_AUTOMATED_TESTS,
      filterConstants.FILTER_TYPE_STATUS,
    ]);
    const ownership = filterFunctions.getOwnershipFilter({
      filterReference: filters,
      filterType: filterConstants.FILTER_AUTOMATED_TESTS,
      isSubaccountDashboard,
      isOpenSauceUser,
      username,
    });
    const owners = filterFunctions.buildOwnerFilter({
      isSubaccountDashboard,
      jobsTeamSharingFeatureEnabled,
      jobsTeamSharingEnabled,
      orgTeams,
      orgUsers,
      user,
      username,
    });
    const showSeeAllButton = isSupportedFeature(features.ARCHIVES) && !isOpenSauceUser;
    const noFilters = filterFunctions.noFilterInUse({
      filters,
      filterType: filterConstants.FILTER_AUTOMATED_TESTS,
      ownership,
      username,
    });
    const rdcLessAvailable = rdcOffset > constants.INITIAL_RDC_OFFSET;
    const performance = filters.getIn([
      filterConstants.FILTER_AUTOMATED_TESTS,
      filterConstants.FILTER_TYPE_PERFORMANCE,
    ]);

    return (
      <div
        className={Styles.wrapper}
        data-qa="automated-jobs-container"
      >
        <Helmet>
          <title>Automated | Test Results</title>
        </Helmet>
        <TitleRow list={crumbsList} />
        <AutomatedJobsTab
          automatedLoading={automatedLoading}
          changeTab={(tabName) => push(`/dashboard/tests/${tabName}`)}
          clearStatuses={(payload) => clearStatuses({
            type: filterConstants.FILTER_AUTOMATED_TESTS,
            filter: payload,
          })}
          cloudType={cloudType}
          filtersInitialized={filtersInitialized}
          isOpenSauceUser={isOpenSauceUser}
          noFilters={noFilters}
          owners={owners}
          ownership={ownership}
          performance={performance}
          rdcLessAvailable={rdcLessAvailable}
          selectOwner={(payload) => selectOwner({
            type: filterConstants.FILTER_AUTOMATED_TESTS,
            data: payload,
          })}
          showOwnersFilter={!isOpenSauceUser}
          showSeeAllButton={showSeeAllButton}
          status={status}
          togglePerformance={togglePerformance}
          toggleStatus={toggleStatus}
          trackedToggleStatus={(payload) => this.toggleStatusFilter({
            type: filterConstants.FILTER_AUTOMATED_TESTS,
            data: payload,
          })}
          username={username}
        />
      </div>
    );
  }
}

FlatJobsContainer.propTypes = {
  automatedLoading: PropTypes.bool.isRequired,
  cleanup: PropTypes.func.isRequired,
  clearRdcOffset: PropTypes.func.isRequired,
  clearStatuses: PropTypes.func.isRequired,
  cloudType: PropTypes.oneOf(constants.CLOUD_TYPES).isRequired,
  experiments: ImmutablePropTypes.map.isRequired,
  fetchAutomatedJobs: PropTypes.func.isRequired,
  filters: ImmutablePropTypes.map.isRequired,
  filtersInitialized: PropTypes.bool.isRequired,
  getUPJobs: PropTypes.func.isRequired,
  initializeFilters: PropTypes.func.isRequired,
  isOpenSauceUser: PropTypes.bool,
  isSubaccountDashboard: PropTypes.bool,
  jobsTeamSharingEnabled: PropTypes.bool.isRequired,
  jobsTeamSharingFeatureEnabled: PropTypes.bool.isRequired,
  location: PropTypes.shape({ pathname: PropTypes.string, search: PropTypes.string }).isRequired,
  match: PropTypes.shape({ params: PropTypes.object }).isRequired,
  organizationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  orgTeams: ImmutablePropTypes.list.isRequired,
  orgUsers: ImmutablePropTypes.list.isRequired,
  push: PropTypes.func.isRequired,
  rdcOffset: PropTypes.number.isRequired,
  selectOwner: PropTypes.func.isRequired,
  setCloudType: PropTypes.func.isRequired,
  shouldFetchOrgUsers: PropTypes.bool.isRequired,
  togglePerformance: PropTypes.func.isRequired,
  toggleStatus: PropTypes.func.isRequired,
  trackedToggleStatus: PropTypes.func.isRequired,
  user: ImmutablePropTypes.recordOf(userRecords.UserRecordClass).isRequired,
  username: PropTypes.string.isRequired,
};

FlatJobsContainer.defaultProps = {
  isOpenSauceUser: false,
  isSubaccountDashboard: false,
  organizationId: null,
};

const mapStateToProps = selectors.containerSelector;
const mapDispatchToProps = {
  cleanup: actions.cleanup,
  clearRdcOffset: actions.clearRdcOffset,
  clearStatuses: filterOperations.clearStatuses,
  getUPJobs: jobsActions.getUPJobs,
  initializeFilters: filterOperations.initializeFilters,
  push: historyPush,
  selectOwner: filterOperations.selectOwner,
  setCloudType: actions.setCloudType,
  toggleStatus: filterOperations.toggleStatus,
  togglePerformance: filterOperations.setPerformanceFilter,
  trackedToggleStatus: filterOperations.trackedToggleJobFilter,
  fetchAutomatedJobs: jobsOperations.fetchAutomatedJobs,
};

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(FlatJobsContainer),
);
export { FlatJobsContainer };
